import { useCallback, useState } from 'react';
import { type WorkQueueGroup, type WorkQueueMeta } from '../../services/workQueue';
import { GroupNav } from './GroupNav';

const PHASE_OPEN_STORAGE_KEY = 'odo:wq:phase-open';
const NAV_FOLDED_STORAGE_KEY = 'odo:wq:nav-folded';

interface GroupNavColumnProps {
    selectedKey: string | null;
    onSelect: (key: string) => void;
    groups: WorkQueueGroup[] | undefined;
    meta: WorkQueueMeta | undefined;
    filterActive: boolean;
    teamFilter: string[];
}

// Per-phase collapse state, persisted so folded phases survive reloads and remounts.
// A phase is open unless its entry is explicitly `false`.
function readPhaseOpen(): Record<string, boolean> {
    if (typeof globalThis === 'undefined' || !globalThis.localStorage) return {};
    try {
        const raw = globalThis.localStorage.getItem(PHASE_OPEN_STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed as Record<string, boolean>;
        }
        return {};
    } catch {
        return {};
    }
}

// Folded by default; only an explicit '0' (the user unfolded it last) starts unfolded.
function readNavFolded(): boolean {
    if (typeof globalThis === 'undefined' || !globalThis.localStorage) return true;
    return globalThis.localStorage.getItem(NAV_FOLDED_STORAGE_KEY) !== '0';
}

export function GroupNavColumn({
    selectedKey,
    onSelect,
    groups,
    meta,
    filterActive,
    teamFilter
}: GroupNavColumnProps) {
    const [phaseOpen, setPhaseOpen] = useState<Record<string, boolean>>(readPhaseOpen);
    const [navFolded, setNavFolded] = useState<boolean>(readNavFolded);

    const handleToggleNavFold = useCallback(() => {
        setNavFolded((prev) => {
            const next = !prev;
            try {
                globalThis.localStorage?.setItem(NAV_FOLDED_STORAGE_KEY, next ? '1' : '0');
            } catch {
                /* ignore */
            }
            return next;
        });
    }, []);

    const handleTogglePhase = useCallback((phase: string) => {
        setPhaseOpen((prev) => {
            const next = { ...prev, [phase]: !(prev[phase] !== false) };
            try {
                globalThis.localStorage?.setItem(PHASE_OPEN_STORAGE_KEY, JSON.stringify(next));
            } catch {
                /* ignore */
            }
            return next;
        });
    }, []);

    return (
        <div
            className="flex-shrink-0 rounded-xl border border-gray-200 bg-white flex flex-col overflow-hidden"
            style={{
                width: navFolded ? 48 : 280,
                transition: 'width 260ms cubic-bezier(0.16, 1, 0.3, 1)'
            }}
        >
            <GroupNav
                selectedKey={selectedKey}
                onSelect={onSelect}
                groups={groups}
                meta={meta}
                phaseOpen={phaseOpen}
                onTogglePhase={handleTogglePhase}
                folded={navFolded}
                onToggleFold={handleToggleNavFold}
                filterActive={filterActive}
                teamFilter={teamFilter}
            />
        </div>
    );
}
